import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getGameFromSlug } from "../../../api/user";
import LeagueShell from "./LeagueShell";
import LeagueColumns from "./LeagueColumns";
import NewPortfolioSection from "../../../components/Games/NewPortfolioSection";

// A league's Portfolio tab — your positions in THIS league, inside the league.
//
// The body is the SAME portfolio the site itself uses — NewPortfolioSection, the
// one behind /portfolio — scoped to this game. So a member sees everything they
// see outside a league (equity, cash, P&L, holdings, open orders, closed trades)
// and can act on it the same way, against the league's own money: every order in
// here carries this league's game_id, and the wallet backing it is the credit the
// league granted on join.
//
// `embedded` keeps it to the portfolio alone — this screen already owns the
// header, the container and the league navigation.
//
// `applyLogic` matches what /portfolio passes, so the current business rules
// (e.g. filled orders excluded from Open Orders) apply here too rather than the
// league quietly running an older set.
const LeaguePortfolio = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setResolved(false);
    (async () => {
      try {
        const res = await getGameFromSlug({ slug });
        // A bad slug answers with HTTP 200 + success:false, so read the body.
        const data = res?.data?.success === false ? null : res?.data?.data;
        if (!cancelled) setGame(data?.id ? data : null);
      } catch (e) {
        console.error("League portfolio lookup failed:", e);
        if (!cancelled) setGame(null);
      } finally {
        if (!cancelled) setResolved(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    if (!resolved) return;
    if (!game) {
      navigate("/contest", { replace: true });
      return;
    }
    // A league's own screens are for MEMBERS. Someone who hasn't joined belongs on
    // the league's front door — which decides between the join preview and the
    // invite gate — not inside its portfolio.
    if (!game.isMember) navigate(`/contest/${game.vanity_url}`, { replace: true });
  }, [resolved, game, navigate]);

  if (!resolved) {
    return (
      <div className="mt-16 md:mt-24 pb-12">
        <div className="max-w-screen-xl mx-auto px-4 md:px-6">
          <div className="h-36 md:h-44 rounded-2xl bg-[#F3F4F6] animate-pulse" />
        </div>
      </div>
    );
  }

  if (!game || !game.isMember) return null;

  return (
    <LeagueShell game={game} active="portfolio" compact /* Portfolio (client Rev 2, Portfolio 2 & 3): eyebrow + league title only */>
      {/* Client Rev 3, mobile inside a private league: "Portfolio has too much
          vertical space after the title block. I would move the Free Cash and
          Portfolio card noticeably upward. The divider can stay, but the gap below
          it should be much tighter. Right now it almost feels like a section is
          missing between the header and the card."
          Measured at 390px BEFORE: divider at y=131, Free Cash card at y=172 — a
          41px gap, of which 24px was this margin and 16px is NewPortfolioSection's
          own top padding. mt-2 leaves ~25px, which lands on the same footing as the
          league's other screens ("Keep the space between the identity block and the
          start of the content consistent on all the screens"). */}
      {/* Client Rev 2, desktop inside a league: "One consistent main column width
          across Home, Portfolio, and Watchlist. Sticky right rail beside that
          column." LeagueColumns is Home's own grid, now shared. `mt-2` is kept —
          it is the tighter gap client Rev 3 asked for on this screen and the rail
          does not change it. Desktop only: below `lg` the grid is one column and
          the rail is not rendered, so mobile Portfolio is exactly as it was. */}
      <LeagueColumns game={game} className="mt-2">
        {/* showSummary: the Free Cash / Portfolio strip. Embedded hosts hide it by
            default (the profile shows those figures in its own header), but this
            tab has no header of its own — and in a league they're the numbers that
            matter, since the money here is the league's, not the main account's.
            fetchPortfolioMetrics already scopes them by game_id. */}
        <NewPortfolioSection
          gameId={game.id}
          slug={game.vanity_url}
          user_id={null}
          isSelfProfile
          ignoreDesign
          applyLogic
          embedded
          showSummary
        />
      </LeagueColumns>
    </LeagueShell>
  );
};

export default LeaguePortfolio;
